/* The console's one durable-conversation vocabulary, and the door contract behind it.
 *
 * One owner answers with `sentientx.sx.conversations.v1`: Autonomy's `AgentService`, whose
 * Project Manager leads the project's agents. A turn picks no preset: the person picks the
 * teammate they talk to (robot design is the robot designer's work, not a `/robot` mode).
 * The contract survives because it was never about how many doors there are: a door
 * states how it is reached, and everything above it — the chat transport, the composer,
 * the thread — is written once.
 */

import type { SxDurableMessage } from "@sx/ui/durable-conversation";

import type { ProjectAttachment } from "@sx/protocol/autonomy/ask";
import type { ContentRef } from "@sx/protocol/common";
import type { ConversationFailure } from "@sx/protocol/conversations";
import type {
  ConversationLiveEdge,
  ConversationStreamEvent,
  ConversationTurnAdmission,
  Narrowed,
} from "../../lib/conversation-wire";
import type { ConsoleDoorId } from "../../platform.gen";

/* The owner's event log is the generated `sentientx.sx.conversations.v1` messages,
 * narrowed once in `lib/conversation-wire`; the chat transport is the one reducer that
 * reads them, and the door below is how it reaches them. */

/** The door that answers with a durable conversation. */
type ConversationDoorId = Extract<ConsoleDoorId, "autonomy">;

/** A turn the owner settled as failed, carried as the refusal it already is.
 *
 * `ConversationFailure` is the conversation wire's typed refusal — code, message,
 * retryable — the same three facts a `RefusalDetail` carries on an RPC path. It arrives
 * on the owner's event log rather than as a Connect error, so nothing on the transport
 * path ever sees it; this is what gives it a type on the way to the screen instead of
 * being dropped into a phase name.
 *
 * It exists because it was silently discarded. Every Loop turn from 04:29Z on 2026-09-07
 * ended in `turn.failed`, and the old session reduced that to "this turn is over" — clearing
 * the error and leaving the phase active. A person saw the thinking shimmer, then
 * nothing, for a refusal the owner had named precisely.
 */
export class ConversationTurnFailure extends Error {
  readonly code: string;
  readonly retryable: boolean;

  constructor(failure: Narrowed<ConversationFailure>) {
    super(failure.message);
    this.name = "ConversationTurnFailure";
    this.code = failure.code;
    this.retryable = failure.retryable;
  }
}

/** The completed Worlds ScanSeed operation that an environment-authoring turn continues. */
export interface WorldAuthoringOperationSource {
  readonly pillar: "worlds";
  readonly kind: "authoring";
  readonly operation_id: string;
}

export interface ConversationTurnInput {
  readonly content: string;
  readonly attachments: readonly ProjectAttachment[];
  readonly environmentSource?: WorldAuthoringOperationSource;
  /** The robot this turn tells what to do, from its page: every turn of its conversation
   * names it. */
  readonly robot?: {
    readonly stationId: string;
    readonly runId: string;
    /** Who decides (`ask.v1.RobotBrain`); the robot operator's own model when unspecified. */
    readonly brain: import("@sx/protocol/autonomy/ask").RobotBrain;
  };
}

/** The staged bytes an attachment names. The proto requires the reference and the
 * generated TypeScript leaves it optional, so an attachment without one is refused.
 *
 * It lives here, beside the turn it reads, and imports only types: the turn outbox
 * that calls it sits under the session in the entry chunk, so a value import from the
 * upload module would drag the generated-RPC runtime into every first paint. */
export function stagedContent(attachment: ProjectAttachment): ContentRef {
  if (attachment.content === undefined) {
    throw new TypeError(`The attachment ${attachment.filename} names no staged content.`);
  }
  return attachment.content;
}

/** What the door offers a person composing a turn: the attachment budget its own schema
 * states. */
export interface OperatorContext {
  readonly name: string;
  readonly mission: string;
  readonly attachment_limits: {
    readonly max_files: number;
    readonly max_file_bytes: number;
    readonly max_turn_bytes: number;
  };
}

/** What one admitted turn was asked with, as the thread shows it.
 *
 * This is the turn half of `ConversationUiMetadata`: the same facts, before the message
 * supplies its `createdAt`. It was a seventh record named for
 * `sentientx.sx.autonomy.ask.v1.TurnOwnerContext` and shaped field for field against it —
 * a second wire schema, which law 7 does not allow — and two of those fields, the owner's
 * `message_id` and its `environment_source`, were written here and read nowhere. The
 * owner's record is the generated message; this is the projection the screen reads, and
 * `conversationContexts` is the one place the two meet. */
export interface ConversationTurnAsked {
  readonly author?: import("@sx/protocol/autonomy/agents").MessageAuthor;
  readonly turnId: string;
  readonly attachments: readonly ProjectAttachment[];
}

export interface ConversationTurnAskedPage {
  readonly contexts: readonly ConversationTurnAsked[];
  readonly next_before: number | null;
}

/** Where the next older page starts. `null` on a leg means that leg is exhausted. */
export interface ConversationHydrationCursor {
  readonly messages: number | null;
  readonly contexts: number | null;
}

export interface ConversationHydrationPage {
  readonly totalMessages: number;
  readonly messages: readonly SxDurableMessage[];
  readonly contexts: readonly ConversationTurnAsked[];
  /** The agent's settings revision each turn on the page ran, by turn id, where the
   * owner records one. */
  readonly settingsRevisions?: Readonly<Record<string, bigint>>;
  readonly next_before: ConversationHydrationCursor;
  /** On the newest page only: where the owner's log stood when the page was read, in the
   * same snapshot. The chat shows the page and observes from there, with nothing to catch
   * up on first. */
  readonly liveEdge?: ConversationLiveEdge;
}

/**
 * One durable conversation, as the console reaches it.
 *
 * A door value is bound to one project and one conversation, so nothing above it
 * re-derives either. Construct it beside the generated client that speaks it —
 * `agentSessionDoor` in `workspaces/agents/door.ts` — and memoize it: the chat is keyed on
 * the door identity.
 */
export interface ConversationDoor {
  readonly id: ConversationDoorId;
  /** The agent as a person names it, for the copy this door's failures produce. */
  readonly agent: string;
  readonly projectId: string;
  readonly conversationId: string;
  /** The door-owned key its transcript hydration hangs from. It names the door, the
   * project and the conversation, which is also this door's whole identity: the chat and
   * its transport are keyed on it, so a caller that forgets to memoize a door cannot make
   * it open and abort streams forever. */
  readonly transcriptQueryKey: readonly unknown[];
  /** The chat's opening read: the newest page, which a door may have read ahead of the
   * chat. Only the first read of a chat takes it; every other read is `hydrate`. */
  readonly open: (signal?: AbortSignal) => Promise<ConversationHydrationPage>;
  /** A page of history from the owner, the newest when `before` is absent. */
  readonly hydrate: (
    before: ConversationHydrationCursor | undefined,
    signal?: AbortSignal,
  ) => Promise<ConversationHydrationPage>;
  /** Observe from one durable cursor. An SSE-backed owner and a long-poll-backed
   * owner both feed the same chat transport; transport is not chat UX. */
  readonly observe: (
    after: number,
    signal?: AbortSignal,
  ) => Promise<ReadableStream<ConversationStreamEvent>>;
  readonly submit: (
    turn: ConversationTurnInput,
    idempotencyKey: string,
  ) => Promise<ConversationTurnAdmission>;
  readonly cancelTurn: (turnId: string, reason: string) => Promise<void>;
}
